import { ColumnsType } from 'antd/es/table';
import { Edit2, Trash } from 'iconsax-react';
import SectionDesc from '../../../components/atoms/SectionDesc';
import Button from '../../../components/atoms/Button';
import CustomTable from '../../../components/atoms/CustomTable';
import { useTranslation } from 'react-i18next';
import { PromotionDuration } from '../../../types/promotionDurations';

interface DurationSectionProps {
  title: string;
  description: string;
  durationColumnTitle: string;
  data: PromotionDuration[];
  isLoading?: boolean;
  activeTab: 'home' | 'category';
  onAdd: () => void;
  onEdit: (item: PromotionDuration) => void;
  onDelete?: (item: PromotionDuration) => void;
}

export default function DurationSection({
  title,
  description,
  durationColumnTitle,
  data,
  isLoading,
  activeTab,
  onAdd,
  onEdit,
  onDelete,
}: DurationSectionProps) {
  const { t } = useTranslation();

  const priceColumn: ColumnsType<PromotionDuration>[number] =
    activeTab === 'home'
      ? {
          title: t('home.duration-main-price'),
          dataIndex: 'price',
          key: 'price',
          render: (_: any, record: PromotionDuration) =>
            `${record.price ?? record.mainMarketplacePrice ?? 0} ${t('SAR')}`,
        }
      : {
          title: t('home.duration-category-price'),
          dataIndex: 'price',
          key: 'price',
          render: (_: any, record: PromotionDuration) =>
            `${record.price ?? record.categoryMarketplacePrice ?? 0} ${t('SAR')}`,
        };

  const columns: ColumnsType<PromotionDuration> = [
    priceColumn,
    {
      title: durationColumnTitle,
      dataIndex: 'duration',
      key: 'duration',
      render: (val: number, record) => {
        const isDay =
          record.durationUnitCode === 'DAY' || record.unit === 'days';
        return `${val} ${isDay ? t('home.duration-unit-days') : t('home.duration-unit-hours')}`;
      },
    },
    {
      title: t('home.action'),
      key: 'action',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center cursor-pointer transition-colors hover:bg-blue-100"
            onClick={() => onEdit(record)}
          >
            <Edit2 size={16} className="text-blue-500" variant="Bold" />
          </div>
          {onDelete && (
            <div
              className="w-8 h-8 rounded-md bg-red-50 flex items-center justify-center cursor-pointer transition-colors hover:bg-red-100"
              onClick={() => onDelete(record)}
            >
              <Trash size={16} className="text-red-500" variant="Bold" />
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-xl p-6 flex flex-col gap-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-[#111928] text-2xl font-bold leading-none">
            {title}
          </h3>
          <SectionDesc text={description} />
        </div>
        <Button variant="dark" className="rounded-xl" onClick={onAdd}>
          {t('home.duration-add')}
        </Button>
      </div>
      <CustomTable
        loading={isLoading}
        columns={columns}
        dataSource={data}
        rowKey="id"
        currentPage={0}
        totalElements={data.length}
        onPageChange={() => {}}
        onRowClick={() => {}}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
}
